<script setup lang="ts">
  import { ref, computed, watch } from 'vue'
  import { storeToRefs } from 'pinia'
  import { useReportThemesStore } from '@stores/reportThemes/reportThemes'
  import { AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion'
  import { Label } from '@/components/ui/label'
  import { Separator } from '@/components/ui/separator'
  import { Select } from "@/components/episto-ui/select";
  import ReportThemeTextStyleRow from './ReportThemeTextStyleRow.vue'
  import type { TextStyle, ReportThemeChartStyles } from '@/types'
  import { MUTED_POINT_COLORS } from '@/lib/chartPalettes'

  defineProps<{ detectedFonts: string[] }>()

  const legendPositionOptions = [
    { value: 'right',  label: 'Right' },
    { value: 'bottom', label: 'Bottom' },
    { value: 'none',   label: 'Hidden' },
  ]

  const store = useReportThemesStore()
  const { currentTheme, localEdits } = storeToRefs(store)

  const FALLBACK_COLOR = MUTED_POINT_COLORS.fill

  const paletteDefault = ref<(string | null)[]>(Array(6).fill(null))
  const paletteNps     = ref<(string | null)[]>(Array(3).fill(null))

  function syncPalettes() {
    const cs = currentTheme.value?.chart_styles ?? {}
    const pd = (cs.palette_default as (string | null)[] | undefined) ?? []
    paletteDefault.value = Array.from({ length: 6 }, (_, i) => pd[i] ?? FALLBACK_COLOR)
    const pn = (cs.palette_nps as (string | null)[] | undefined) ?? []
    paletteNps.value = Array.from({ length: 3 }, (_, i) => pn[i] ?? FALLBACK_COLOR)
  }

  // Fires on mount (theme already loaded when this section renders) and again
  // after a PPTX re-upload replaces chart_styles.
  watch(() => currentTheme.value?.chart_styles, syncPalettes, { immediate: true })

  function chartStyle(key: keyof ReportThemeChartStyles): TextStyle {
    return ((localEdits.value?.chart_styles as Record<string, TextStyle> | undefined)?.[key] ?? {}) as TextStyle
  }

  function setChartStyle(key: keyof ReportThemeChartStyles, value: TextStyle) {
    if (!localEdits.value) return
    if (!localEdits.value.chart_styles) localEdits.value.chart_styles = {}
    ;(localEdits.value.chart_styles as Record<string, unknown>)[key] = value
  }

  const legendPosition = computed(() =>
    (localEdits.value?.chart_styles as { legend_style?: { position?: string } } | undefined)
      ?.legend_style?.position ?? null
  )

  function setLegendPosition(pos: string | null) {
    const current = chartStyle('legend_style')
    setChartStyle('legend_style', { ...current, position: pos } as TextStyle)
  }

  function setPaletteColor(type: 'default' | 'nps', index: number, value: string | null) {
    if (type === 'default') paletteDefault.value[index] = value
    else paletteNps.value[index] = value

    if (!localEdits.value) return
    if (!localEdits.value.chart_styles) localEdits.value.chart_styles = {}
    ;(localEdits.value.chart_styles as Record<string, unknown>)[
      type === 'default' ? 'palette_default' : 'palette_nps'
    ] = type === 'default' ? [...paletteDefault.value] : [...paletteNps.value]
  }
</script>

<template>
  <AccordionItem value="charts">
    <AccordionTrigger class="text-base font-semibold">Charts</AccordionTrigger>
    <AccordionContent>
      <div class="space-y-5 pt-1">

        <!-- Palettes -->
        <div class="grid grid-cols-2 gap-6">
          <div>
            <Label class="mb-2 block">Default palette</Label>
            <div class="flex items-center gap-2 flex-wrap">
              <div
                v-for="(color, i) in paletteDefault"
                :key="i">
                <label
                  class="w-7 h-7 rounded-full cursor-pointer block ring-1 ring-border transition-transform hover:scale-110"
                  :style="{ backgroundColor: color }">
                  <input
                    type="color"
                    class="sr-only"
                    :value="color"
                    @input="(e) => setPaletteColor('default', i, (e.target as HTMLInputElement).value)" />
                </label>
              </div>
            </div>
          </div>
          <div>
            <Label class="mb-2 block">NPS palette</Label>
            <div class="flex items-center gap-2">
              <div
                v-for="(color, i) in paletteNps"
                :key="i"
                :title="['Detractors', 'Passives', 'Promoters'][i]">
                <label
                  class="w-7 h-7 rounded-full cursor-pointer block ring-1 ring-border transition-transform hover:scale-110"
                  :style="{ backgroundColor: color }">
                  <input
                    type="color"
                    class="sr-only"
                    :value="color"
                    @input="(e) => setPaletteColor('nps', i, (e.target as HTMLInputElement).value)" />
                </label>
              </div>
            </div>
          </div>
        </div>

        <Separator />

        <!-- Text styles -->
        <div>
          <Label>Texts</Label>
          <div class="mt-2">
            <!-- Column headers -->
            <div class="flex items-center gap-4 pb-1 mb-1">
              <span class="w-24 shrink-0" />
              <span class="w-10 shrink-0 text-xs text-muted-foreground text-center">Color</span>
              <span class="w-44 shrink-0 text-xs text-muted-foreground">Font</span>
              <span class="w-14 shrink-0 text-xs text-muted-foreground">Size</span>
              <span class="w-10 shrink-0 text-xs text-muted-foreground">Bold</span>
              <span class="w-28 shrink-0 text-xs text-muted-foreground">Position</span>
            </div>
            <ReportThemeTextStyleRow
              label="Data labels"
              :style="chartStyle('data_label_style')"
              :detected-fonts="detectedFonts"
              @update="(v) => setChartStyle('data_label_style', v)">
              <span class="w-28 shrink-0" />
            </ReportThemeTextStyleRow>
            <ReportThemeTextStyleRow
              label="Axis labels"
              :style="chartStyle('axis_label_style')"
              :detected-fonts="detectedFonts"
              @update="(v) => setChartStyle('axis_label_style', v)">
              <span class="w-28 shrink-0" />
            </ReportThemeTextStyleRow>
            <ReportThemeTextStyleRow
              label="Legend"
              :style="chartStyle('legend_style')"
              :detected-fonts="detectedFonts"
              @update="(v) => setChartStyle('legend_style', v)">
              <Select
                :model-value="legendPosition"
                :options="legendPositionOptions"
                placeholder="Default"
                clearable
                class="w-28 shrink-0"
                @update:model-value="(v) => setLegendPosition(v)" />
            </ReportThemeTextStyleRow>
          </div>
        </div>
      </div>
    </AccordionContent>
  </AccordionItem>
</template>
